'use client';

import { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const Contact = (): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setTimeout(() => setIsSubmitted(false), 8000);
      } else {
        console.error('Failed to send contact email');
        // Still show success to user for better UX
        setIsSubmitted(true);
        setTimeout(() => setIsSubmitted(false), 8000);
      }
    } catch (error) {
      console.error('Error sending contact email:', error);
      // Still show success to user for better UX
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '718-517-9235',
      description: 'Call us anytime for immediate assistance',
      href: 'tel:718-517-9235'
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'dira4ubp@gmail.com',
      description: 'Send us an email and we\'ll respond within 24 hours',
      href: 'mailto:dira4ubp@gmail.com'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Monroe, NY',
      description: 'Located in the heart of Monroe, New York',
      href: '#'
    },
    {
      icon: Clock,
      title: 'Response Time',
      value: '24 Hours',
      description: 'We typically respond to all inquiries within one business day',
      href: '#'
    }
  ];

  return (
    <section id="contact" ref={sectionRef} className="py-20 bg-[#F9F6F2] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6">
              Contact Us
            </h2>
            <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto leading-relaxed">
              Have questions about your stay? We're here to help with your Mountain View booking and any questions you may have.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-[#333333] mb-8">Get In Touch</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {contactInfo.map((item, index) => (
                    <a
                      key={index}
                      href={item.href}
                      className={`group bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                        item.href === '#' ? 'cursor-default' : 'cursor-pointer'
                      } delay-${index * 100}`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="bg-[#7C8F75]/10 p-3 rounded-lg group-hover:bg-[#7C8F75] transition-colors duration-300">
                          <item.icon className="h-6 w-6 text-[#7C8F75] group-hover:text-white transition-colors duration-300" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-[#333333] mb-1 group-hover:text-[#7C8F75] transition-colors duration-300">
                            {item.title}
                          </h4>
                          <p className="text-[#333333] font-medium mb-1">{item.value}</p>
                          <p className="text-[#333333]/70 text-sm">{item.description}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Contact */}
              <div className="bg-[#7C8F75] text-white p-8 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Need Immediate Assistance?</h3>
                <p className="mb-6 opacity-90">
                  For urgent booking inquiries or immediate assistance, don't hesitate to call us directly.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-white text-[#7C8F75] hover:bg-white/90 flex-1 transform hover:scale-105 transition-all duration-300"
                  >
                    <a href="tel:718-517-9235" className="flex items-center justify-center">
                      <Phone className="h-5 w-5 mr-2" />
                      Call Now
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-[#7C8F75] flex-1 transform hover:scale-105 transition-all duration-300"
                  >
                    <a href="mailto:dira4ubp@gmail.com" className="flex items-center justify-center">
                      <Mail className="h-5 w-5 mr-2" />
                      Email Us
                    </a>
                  </Button>
                </div>
              </div>

              {/* Location Info */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold text-[#333333] mb-4">Prime Location Benefits</h3>
                <ul className="space-y-3 text-[#333333]/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>4 minutes to KJ Munro community</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>7 minutes to Woodbury Commons Premium Outlets</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>5 minutes to רוח החיים Park</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>Easy access to dining and entertainment</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-lg shadow-xl">
              <h3 className="text-2xl font-bold text-[#333333] mb-8">Send Us a Message</h3>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-[#333333] mb-4">Message Sent Successfully!</h4>
                  <p className="text-[#333333]/80 mb-4">Thank you for contacting us. We'll get back to you within 24 hours.</p>
                  <div className="bg-[#7C8F75]/10 p-4 rounded-xl inline-block">
                    <p className="text-[#333333] font-medium text-sm">A confirmation email has been sent to your inbox</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName" className="text-[#333333] font-medium">First Name</Label>
                      <Input
                        id="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="mt-2 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-[#333333] font-medium">Last Name</Label>
                      <Input
                        id="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="mt-2 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contactEmail" className="text-[#333333] font-medium">Email Address</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="mt-2 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contactPhone" className="text-[#333333] font-medium">Phone Number</Label>
                    <Input
                      id="contactPhone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="mt-2 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#333333] font-medium">Subject</Label>
                    <Input
                      id="subject"
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      className="mt-2 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                      placeholder="What's this about?"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contactMessage" className="text-[#333333] font-medium">Message</Label>
                    <Textarea
                      id="contactMessage"
                      className="mt-2 border-[#7C8F75]/30 focus:border-[#7C8F75]"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tell us how we can help you..."
                      rows={5}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-[#7C8F75] hover:bg-[#7C8F75]/90 text-white py-4 text-lg font-medium transform hover:scale-105 transition-all duration-300"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Wave transition to Footer */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
        <svg 
          className="relative block w-full h-32" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="contactWaveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F9F6F2" stopOpacity="1" />
              <stop offset="20%" stopColor="rgba(249, 246, 242, 0.9)" stopOpacity="0.9" />
              <stop offset="40%" stopColor="rgba(51, 51, 51, 0.3)" stopOpacity="0.5" />
              <stop offset="60%" stopColor="rgba(51, 51, 51, 0.6)" stopOpacity="0.7" />
              <stop offset="80%" stopColor="rgba(51, 51, 51, 0.9)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#333333" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="contactWaveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(232, 227, 221, 0.8)" stopOpacity="0.8" />
              <stop offset="30%" stopColor="rgba(124, 143, 117, 0.1)" stopOpacity="0.4" />
              <stop offset="60%" stopColor="rgba(51, 51, 51, 0.5)" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#333333" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="contactWaveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(249, 246, 242, 0.6)" stopOpacity="0.6" />
              <stop offset="50%" stopColor="rgba(51, 51, 51, 0.8)" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#333333" stopOpacity="1" />
            </linearGradient>
          </defs>
          
          {/* Base wave */}
          <path 
            d="M0,60L48,65C96,70,192,80,288,75C384,70,480,50,576,45C672,40,768,50,864,60C960,70,1056,80,1152,75C1200,70,1248,50,1296,45L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#contactWaveGradient1)"
          />
          
          {/* Second wave layer */}
          <path 
            d="M0,40L48,45C96,50,192,60,288,55C384,50,480,30,576,25C672,20,768,30,864,40C960,50,1056,60,1152,55C1200,50,1248,30,1296,25L1344,20L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#contactWaveGradient2)"
          />
          
          {/* Final wave */}
          <path 
            d="M0,20L48,25C96,30,192,40,288,35C384,30,480,10,576,5C672,0,768,10,864,20C960,30,1056,40,1152,35C1200,30,1248,10,1296,5L1344,0L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#contactWaveGradient3)"
          />
        </svg>
      </div>
    </section>
  );
};

export default Contact;